import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from 'features/components/Button';
import { Dropdown } from 'features/entry-form-v2/component/Dropdown';
import { ROUTE_MAP } from 'features/entry-form-v2/utils/routeMap';
import { StrictMode, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { LanguageType } from 'types/enums';
import { z } from 'zod';
import { Input } from '../../component/Input';
import { LedidiRequestError } from '../../data/makeRequest';
import { useSurveyContext } from '../components/SurveyContext';
import { useSurveyForm } from '../data/useSurveyForm';

export const SurveyPinEntryPage = () => {
	const { language, surveyId, pinCode, setPinCode, clearPinCode } = useSurveyContext();
	const dictionary = DICTIONARY[language];
	const navigate = useNavigate();

	const surveyDataQuery = useSurveyForm({
		surveyId,
		pinCode: pinCode || undefined
	});

	const {
		register,
		handleSubmit,
		formState: { errors }
	} = useForm<EnterPinCodeForm>({
		resolver: zodResolver(createEnterPinCodeSchema(dictionary))
	});

	const onSubmit = handleSubmit(data => {
		setPinCode(data.pinCode);
	});

	useEffect(() => {
		if (surveyDataQuery.isSuccess) {
			navigate(ROUTE_MAP.surveys.byId.form.createPath({ surveyId, language }));
		}

		if (surveyDataQuery.error) {
			if (surveyDataQuery.error instanceof LedidiRequestError) {
				const hasWrongPinCodeError = surveyDataQuery.error.errors.some(
					err => err.code === 'error.wrongPinCode'
				);
				const hasExpiredPinCodeError = surveyDataQuery.error.errors.some(
					err => err.code === 'error.expiredPinCode'
				);
				const hasSurveyAlreadyCompletedError = surveyDataQuery.error.errors.some(
					err => err.code === 'error.surveyAlreadyCompleted'
				);
				const hasMaxAttemptsExceededError = surveyDataQuery.error.errors.some(
					err => err.code === 'error.maxAttemptsExceeded'
				);
				const hasNoVariablesError = surveyDataQuery.error.errors.some(
					err => err.code === 'error.variableNotFound'
				);
				const hasNoFormError = surveyDataQuery.error.errors.some(
					err => err.code === 'error.formNotFound'
				);

				if (hasNoVariablesError) {
					clearPinCode();
					navigate(ROUTE_MAP.surveys.byId.noVariables.createPath({ surveyId, language }));
				}

				if (hasNoFormError) {
					clearPinCode();
					navigate(ROUTE_MAP.surveys.byId.noForm.createPath({ surveyId, language }));
				}

				if (hasWrongPinCodeError) {
					clearPinCode();
					navigate(ROUTE_MAP.surveys.byId.invalidPin.createPath({ surveyId, language }));
				}

				if (hasExpiredPinCodeError) {
					clearPinCode();
					navigate(ROUTE_MAP.surveys.byId.expired.createPath({ surveyId, language }));
				}

				if (hasSurveyAlreadyCompletedError) {
					clearPinCode();
					navigate(ROUTE_MAP.surveys.byId.completed.createPath({ surveyId, language }));
				}

				if (hasMaxAttemptsExceededError) {
					clearPinCode();
					navigate(
						ROUTE_MAP.surveys.byId.maxAttemptsExceeded.createPath({
							surveyId,
							language
						})
					);
				}
			}
		}
	}, [
		surveyDataQuery.isSuccess,
		surveyDataQuery.error,
		navigate,
		surveyId,
		language,
		clearPinCode
	]);

	return (
		<>
			<div className="flex flex-col p-8 sm:pt-12 md:pt-20 md:w-[756px] mx-auto">
				<LanguageSelector />
				<div className="flex flex-col gap-4 sm:gap-6 p-4 sm:p-6 bg-white rounded-lg shadow-normal">
					<h1 className="text-xl sm:text-2xl font-semibold text-gray-900">
						{dictionary.enterSurveyPinCode}
					</h1>
					<p className="text-sm sm:text-base text-gray-600">
						{dictionary.enterPinCodeDescription}
					</p>

					<form onSubmit={onSubmit} className="flex flex-col gap-4">
						<Input
							{...register('pinCode')}
							id="pin-code"
							label={dictionary.pinCode}
							type="text"
							placeholder={dictionary.enterPinCode}
							obligatory
							error={errors.pinCode?.message}
						/>

						<Button
							text={dictionary.continue}
							type="submit"
							variant="primary"
							className="w-full sm:w-auto sm:self-end"
							loading={surveyDataQuery.isFetching}
						/>
					</form>
				</div>
			</div>
		</>
	);
};

const LanguageSelector = () => {
	const { language, setLanguage } = useSurveyContext();
	const dictionary = DICTIONARY[language];

	const handleLanguageChange = (newLanguage: LanguageType) => {
		setLanguage(newLanguage);
	};

	const getLanguageDisplayName = (lang: LanguageType) => {
		return lang === LanguageType.English ? 'English' : 'Norsk';
	};

	return (
		<StrictMode>
			<div className="flex items-center justify-end gap-3 mb-4">
				<label className="text-base font-medium text-gray-700 mt-2">
					{dictionary.language}:
				</label>
				<div className="relative">
					<Dropdown
						align="right"
						placeholder={dictionary.currentLanguage}
						value={getLanguageDisplayName(language)}
					>
						<Dropdown.Item onClick={() => handleLanguageChange(LanguageType.English)}>
							English
						</Dropdown.Item>
						<Dropdown.Item onClick={() => handleLanguageChange(LanguageType.Norwegian)}>
							Norsk
						</Dropdown.Item>
					</Dropdown>
				</div>
			</div>
		</StrictMode>
	);
};

type Translation = (typeof DICTIONARY)[keyof typeof DICTIONARY];

const createEnterPinCodeSchema = (translation: Translation) =>
	z.object({
		pinCode: z.string().regex(/^\d{6}$/, translation.pinCodeMustBe6Digits)
	});

type EnterPinCodeForm = z.infer<ReturnType<typeof createEnterPinCodeSchema>>;

const DICTIONARY = {
	[LanguageType.English]: {
		language: 'Language',
		currentLanguage: 'English',
		enterSurveyPinCode: 'Enter survey pin code',
		enterPinCodeDescription: 'Please enter the pin code provided to access this survey.',
		pinCode: 'Pin code',
		enterPinCode: 'Enter pin code',
		continue: 'Continue',
		pinCodeMustBe6Digits: 'Pin code must be exactly 6 digits'
	},
	[LanguageType.Norwegian]: {
		language: 'Språk',
		currentLanguage: 'Norsk',
		enterSurveyPinCode: 'Skriv inn undersøkelsens pin-kode',
		enterPinCodeDescription:
			'Vennligst skriv inn pin-koden som ble gitt for å få tilgang til undersøkelsen.',
		pinCode: 'Pin-kode',
		enterPinCode: 'Skriv inn pin-kode',
		continue: 'Fortsett',
		pinCodeMustBe6Digits: 'Pin-koden må være nøyaktig 6 siffer'
	}
};
